import { useState, useEffect, useRef, useCallback } from "react"
import ConversationList from "../components/ConversationList"
import MessagePanel from "../components/MessagePanel"
import NewChat from "../components/NewChat"
import { API_URL, WS_URL } from "../config"

function Chat({ token, userId, nombre, onLogout }) {
  const [conversaciones, setConversaciones] = useState([])
  const [conversacionActiva, setConversacionActiva] = useState(null)
  const [mensajes, setMensajes] = useState([])
  const [mostrarNuevoChat, setMostrarNuevoChat] = useState(false)
  const [mostrarPerfil, setMostrarPerfil] = useState(false)
  const [copiado, setCopiado] = useState(false)
  const [wsEstado, setWsEstado] = useState("conectando")
  const [contactNames, setContactNames] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jaureguichat_contactNames") || "{}") } catch { return {} }
  })

  const ws = useRef(null)
  const conversacionActivaRef = useRef(null)
  const reconnectRef = useRef(null)

  conversacionActivaRef.current = conversacionActiva

  const getOtroUserId = (conversationId) => {
    const partes = conversationId.split("#")
    return partes.find(id => id !== userId) || partes[partes.length - 1]
  }

  const guardarNombre = useCallback((uid, name) => {
    if (!name || !uid) return
    setContactNames(prev => {
      if (prev[uid] === name) return prev
      const next = { ...prev, [uid]: name }
      localStorage.setItem("jaureguichat_contactNames", JSON.stringify(next))
      return next
    })
  }, [])

  const conectarWS = useCallback(() => {
    if (ws.current?.readyState === WebSocket.OPEN) return

    const socket = new WebSocket(`${WS_URL}?userId=${userId}`)
    ws.current = socket
    setWsEstado("conectando")

    socket.onopen = () => setWsEstado("conectado")

    socket.onmessage = (event) => {
      const dato = JSON.parse(event.data)

      if (dato.action === "newMessage") {
        if (dato.nombre) guardarNombre(dato.from, dato.nombre)
        setMensajes(prev =>
          conversacionActivaRef.current === dato.conversationId
            ? [...prev, dato]
            : prev
        )
        setConversaciones(prev => {
          const existe = prev.find(c => c.conversationId === dato.conversationId)
          if (!existe) {
            return [{ conversationId: dato.conversationId, ultimoMensaje: dato.texto, timestamp: dato.timestamp }, ...prev]
          }
          return prev.map(c =>
            c.conversationId === dato.conversationId
              ? { ...c, ultimoMensaje: dato.texto, timestamp: dato.timestamp }
              : c
          )
        })
      }

      if (dato.action === "messages") {
        setMensajes(dato.mensajes || [])
      }
    }

    socket.onclose = () => {
      setWsEstado("desconectado")
      reconnectRef.current = setTimeout(conectarWS, 3000)
    }

    socket.onerror = () => socket.close()
  }, [userId, guardarNombre])

  useEffect(() => {
    conectarWS()
    return () => {
      clearTimeout(reconnectRef.current)
      ws.current?.close()
    }
  }, [conectarWS])

  // Cargar conversaciones al entrar
  useEffect(() => {
    
    fetch(`${API_URL}/conversaciones/${userId}`)
      .then(r => r.json())
      .then(data => {
        setConversaciones(data.conversaciones.map(c => ({
          conversationId: c.conversationId,
          ultimoMensaje: c.ultimoMensaje,
          timestamp: c.timestamp
        })))
      })
      .catch(err => console.error(err))
  }, [userId])

  const abrirConversacion = (conversationId) => {
    setConversacionActiva(conversationId)
    setMensajes([])
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({ action: "getMessages", conversationId }))
    }
  }

  const enviarMensaje = (texto) => {
    const to = getOtroUserId(conversacionActiva)
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({ action: "sendMessage", to, texto }))
    }
    const msg = { from: userId, texto, timestamp: new Date().toISOString() }
    setMensajes(prev => [...prev, msg])
    setConversaciones(prev =>
      prev.map(c =>
        c.conversationId === conversacionActiva
          ? { ...c, ultimoMensaje: texto, timestamp: msg.timestamp }
          : c
      )
    )
  }

  const iniciarChat = (toUserId, nombreContacto) => {
    guardarNombre(toUserId, nombreContacto)
    const ids = [userId, toUserId].sort()
    const conversationId = ids.join("#")
    setMostrarNuevoChat(false)
    if (!conversaciones.find(c => c.conversationId === conversationId)) {
      setConversaciones(prev => [{ conversationId, ultimoMensaje: "", timestamp: null }, ...prev])
    }
    abrirConversacion(conversationId)
  }

  const copiarId = () => {
    navigator.clipboard.writeText(userId)
    setCopiado(true)
    setTimeout(() => setCopiado(false), 2000)
  }

  const otroIdActivo = conversacionActiva ? getOtroUserId(conversacionActiva) : null
  const nombreContactoActivo = otroIdActivo ? (contactNames[otroIdActivo] || otroIdActivo) : ""

  return (
    <div className="chat-container">
      <div className="sidebar">
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <span className="sidebar-logo">J</span>
            <span>Jaureguichat</span>
          </div>
          <div className="sidebar-actions">
            <button
              className={`icon-btn ${mostrarPerfil ? "active" : ""}`}
              title="Tu perfil"
              onClick={() => setMostrarPerfil(v => !v)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            </button>
            <button
              className="icon-btn"
              title="Nueva conversación"
              onClick={() => setMostrarNuevoChat(true)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                <line x1="12" y1="8" x2="12" y2="14"/>
                <line x1="9" y1="11" x2="15" y2="11"/>
              </svg>
            </button>
          </div>
        </div>

        {mostrarPerfil && (
          <div className="profile-panel">
            <div className="profile-avatar">{(nombre || userId)?.[0]?.toUpperCase()}</div>
            {nombre && <div className="profile-nombre">{nombre}</div>}
            <div className="profile-id-label">Tu ID anónimo</div>
            <div className="profile-id-row">
              <code className="profile-id">{userId}</code>
              <button className="copy-btn" onClick={copiarId} title="Copiar">
                {copiado ? "✓ Copiado" : "⎘ Copiar"}
              </button>
            </div>
            <div className={`ws-badge ${wsEstado}`}>
              <span className="ws-dot" />
              {wsEstado === "conectado" ? "En línea" : wsEstado === "conectando" ? "Conectando..." : "Sin conexión"}
            </div>
            <button className="btn-logout" onClick={onLogout}>Cerrar sesión</button>
          </div>
        )}

        <ConversationList
          conversaciones={conversaciones}
          activa={conversacionActiva}
          userId={userId}
          contactNames={contactNames}
          onSelect={abrirConversacion}
        />
      </div>

      <div className="main">
        {conversacionActiva
          ? <MessagePanel
              mensajes={mensajes}
              userId={userId}
              contactName={nombreContactoActivo}
              onEnviar={enviarMensaje}
              onBack={() => setConversacionActiva(null)}
            />
          : <div className="empty-state">
              <div className="empty-icon">💬</div>
              <h3>Bienvenido a Jaureguichat</h3>
              <p>Selecciona una conversación o inicia una nueva</p>
              <button className="btn-primary" onClick={() => setMostrarNuevoChat(true)}>
                + Nueva conversación
              </button>
            </div>
        }
      </div>

      {mostrarNuevoChat && (
        <NewChat
          onIniciar={iniciarChat}
          onCerrar={() => setMostrarNuevoChat(false)}
        />
      )}
    </div>
  )
}

export default Chat
